import { computed, inject, Injectable, signal } from "@angular/core";
import { Validators } from "@angular/forms";
import { format } from "date-fns";
import { Observable } from "rxjs";

// EZ
import { EzFormDialogService } from "@template-ui/services";
import {
  EzFormDialogButtonConfig,
  EzFormDialogMode,
  EzFormInputType,
  EzFormProgressDialogCompletion,
  EzFormProgressDialogRequest,
  InputChangeEvent,
  InputConfig
} from "@template-ui/types";

// BITS
import { AppAngularBaseClass } from "@app/core/services/app-angular-base";
import { requiredWhen, requiredWhenEquals } from "@app/shared/forms/cross-validators";
import { ActivityLogAddActivityEndpointParamConfig, ActivityLogContext } from "./activity-log-grid.types";

const DEFAULT_OTHER_DOCUMENT_TYPE_CODE = "OTHER";
const DEFAULT_DIALOG_HEADER = "Add Activity";
const DEFAULT_DIALOG_WIDTH = "4x";
const DATE_TIME_FORMAT = "yyyy-MM-dd HH:mm:ss";

type DocTypeOption = {
  docTyc?: string;
  docName?: string;
  value?: string;
  viewValue?: string;
};

export interface OpenActivityLogDialogOptions {
  activityLogContext: ActivityLogContext;
  addActivityEndpointParams?: ActivityLogAddActivityEndpointParamConfig[];
  docTypes?: unknown;
  submitEndpoint: string;
  postByUrl: (endpoint: string, formData: FormData) => Observable<unknown>;
  otherDocumentTypeCode?: string;
  header?: string;
}

/**
 * Encapsulates the Add Activity dialog workflow used by `ActivityLogGridComponent`.
 *
 * This is implemented as an injectable service (instead of module-level functions)
 * to favor OOP/DI for feature logic and to leave
 * room for future stateful concerns (for example, cached options).
 *
 * The service is intentionally colocated with the component because the behavior
 * is currently component-specific UI orchestration, not a cross-feature shared
 * domain service.
 */
@Injectable({ providedIn: "root" })
export class ActivityLogGridDialogService extends AppAngularBaseClass {
  // ----------------------------------------------------------------
  // Dependency Injection
  // ----------------------------------------------------------------

  private readonly ezFormDialog = inject(EzFormDialogService);

  // ----------------------------------------------------------------
  // Public Methods
  // ----------------------------------------------------------------

  /**
   * Opens the standardized Add Activity dialog flow and submits to the supplied endpoint.
   */
  public openActivityLogDialog(
    options: OpenActivityLogDialogOptions
  ): Promise<EzFormProgressDialogCompletion<unknown, unknown> | null> {
    const request = this.buildActivityLogDialogRequest(options);
    return this.ezFormDialog.openProgress(request);
  }

  // ----------------------------------------------------------------
  // Private Helper Methods
  // ----------------------------------------------------------------

  /** Creates the progress-dialog request object for the Add Activity workflow. */
  private buildActivityLogDialogRequest(
    options: OpenActivityLogDialogOptions
  ): EzFormProgressDialogRequest<Record<string, unknown>, unknown, unknown> {
    const otherDocumentTypeCode = options.otherDocumentTypeCode ?? DEFAULT_OTHER_DOCUMENT_TYPE_CODE;
    const header = options.header ?? DEFAULT_DIALOG_HEADER;

    const selectedDocumentTypeCode = signal<string | undefined>(undefined);
    const showOtherDescription = computed(() => selectedDocumentTypeCode() === otherDocumentTypeCode);

    const formInputConfigs: InputConfig[] = [

      // Row 1
      {
        name: "addDate",
        label: "Date",
        type: EzFormInputType.DATE_TIME,
        dateMax: new Date(),
        placeholder: "Select Date and Time",
        validation: [Validators.required],
        validationMessages: { required: "Date is required." },
        width: "1x",
        row: 1,
        defaultValue: new Date()
      },

      // Row 2
      {
        name: "activityDescription",
        label: "Enter Activity Description here",
        type: EzFormInputType.TEXTAREA,
        validation: [Validators.required, Validators.maxLength(4000)],
        validationMessages: {
          required: "Activity Description is required.",
          maxlength: "Activity Description cannot be more than 4000 characters."
        },
        width: "4x",
        row: 2,
        showCharactersLeft: true
      },

      // Row 3
      {
        name: "fileChooser",
        label: "Upload Document (Optional)",
        type: EzFormInputType.FILE_CHOOSER,
        row: 3,
        width: "3x",
        fileChooserConfig: {
          autoUpload: false
        }
      },

      {
        name: "documentType",
        label: "Document Type",
        type: EzFormInputType.SELECT,
        options: this.mapDocTypeOptions(options.docTypes),
        placeholder: "Select a Document Type",
        crossValidators: [
          requiredWhen(
            "fileChooser",
            (fileChooserValue) => !!this.extractFileFromFileChooserValue(fileChooserValue),
            "requiredWhenFileSelected"
          ),
        ],
        validationMessages: { requiredWhenFileSelected: "Document Type is required when a file is selected." },
        width: "1x",
        row: 3
      },

      // Row 4
      {
        name: "otherDescription",
        type: EzFormInputType.TEXT,
        label: "Other Description",
        placeholder: "Enter Description",
        row: 4,
        width: "4x",
        validation: [Validators.maxLength(100)],
        crossValidators: [requiredWhenEquals("documentType", otherDocumentTypeCode, "requiredWhenOther")],
        validationMessages: {
          requiredWhenOther: "Other Description is required when Document Type is Other.",
          maxlength: "Other Description cannot exceed 100 characters."
        },
        show: showOtherDescription
      }
    ];

    return {
      initiateDialog: {
        mode: EzFormDialogMode.CALLBACK,
        header,
        width: DEFAULT_DIALOG_WIDTH,
        formInputConfigs,
        onInputChange: (event: InputChangeEvent<unknown>) => {
          if (event.name === "documentType") {
            selectedDocumentTypeCode.set(this.toStringOrUndefined(event.value));
          }
        },
        headerButtons: [
          { id: "addActivity", buttonText: "Add Activity", validFormRequired: true, startsProgressFlow: true },
          { id: "cancel", buttonText: "Cancel", startsProgressFlow: false },
        ] satisfies EzFormDialogButtonConfig[]
      },
      processingDialog: { html: "Please wait while we add the activity..." },
      successDialog: { html: "New activity was added." },
      errorDialog: { html: "An error occurred during the operation." },
      onStartProcessing: ({ initiateResult }) => {
        const payload = initiateResult?.payload ?? {};
        const formData = this.buildActivityLogFormData(payload, options.activityLogContext, options.addActivityEndpointParams);
        return options.postByUrl(options.submitEndpoint, formData);
      }
    };
  }

  /** Builds the multipart payload expected by the Add Activity API. */
  private buildActivityLogFormData(
    payload: Record<string, unknown>,
    activityLogContext: ActivityLogContext,
    addActivityEndpointParams: ActivityLogAddActivityEndpointParamConfig[] | undefined
  ): FormData {
    const formData = new FormData();
    const selectedDate = this.resolveDate(payload["addDate"]);
    const addDate = selectedDate ? format(selectedDate, DATE_TIME_FORMAT) : format(new Date(), DATE_TIME_FORMAT);
    const file = this.extractFileFromFileChooserValue(payload["fileChooser"]);

    formData.append("AddDate", addDate);
    formData.append("DocTyc", this.toStringOrUndefined(payload["documentType"]) ?? "");
    formData.append("Notes", this.toStringOrUndefined(payload["activityDescription"]) ?? "");
    formData.append("OtherTxt", this.toStringOrUndefined(payload["otherDescription"]) ?? "");
    if (!this.hasEndpointParamMapping(addActivityEndpointParams, "PartId")) {
      formData.append("PartId", String(this.user.getPartId() ?? 0));
    }
    this.appendConfiguredContextParams(formData, activityLogContext, addActivityEndpointParams);

    if (file) {
      formData.append("File", file);
    }

    return formData;
  }

  /** Maps doc-type response payloads to select options used by the dialog form. */
  private mapDocTypeOptions(docTypes: unknown): { value: string; viewValue: string }[] {
    const items = this.extractDocTypeItems(docTypes);
    if (!items.length) {
      return [];
    }

    return items
      .map((docType) => this.normalizeDocTypeOption(docType))
      .filter((opt): opt is { value: string; viewValue: string } => !!opt);
  }

  /** Extracts doc-type arrays from supported response wrapper shapes. */
  private extractDocTypeItems(docTypes: unknown): unknown[] {
    if (Array.isArray(docTypes)) {
      return docTypes;
    }

    if (!docTypes || typeof docTypes !== "object") {
      return [];
    }

    const wrapped = docTypes as {
      documentTypeList?: unknown;
      data?: unknown;
      value?: unknown;
    };

    if (Array.isArray(wrapped.documentTypeList)) {
      return wrapped.documentTypeList;
    }

    if (Array.isArray(wrapped.data)) {
      return wrapped.data;
    }

    if (Array.isArray(wrapped.value)) {
      return wrapped.value;
    }

    return [];
  }

  /** Normalizes a raw doc-type item into an EZ select option. */
  private normalizeDocTypeOption(docType: unknown): { value: string; viewValue: string } | null {
    if (!docType || typeof docType !== "object") {
      return null;
    }

    const option = docType as DocTypeOption;
    const value = option.docTyc ?? option.value;
    const viewValue = option.docName ?? option.viewValue;
    if (!value || !viewValue) {
      return null;
    }

    return { value, viewValue };
  }

  /** Resolves supported date values into a valid `Date` instance. */
  private resolveDate(value: unknown): Date | null {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }

    if (typeof value === "string" || typeof value === "number") {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
  }

  /** Extracts the selected file from the EZ file-chooser value shape. */
  private extractFileFromFileChooserValue(value: unknown): File | null {
    if (!value || typeof value !== "object") {
      return null;
    }

    const fileChooserValue = value as { document?: unknown };
    return fileChooserValue.document instanceof File ? fileChooserValue.document : null;
  }

  /** Returns a non-empty string, otherwise `undefined`. */
  private toStringOrUndefined(value: unknown): string | undefined {
    return typeof value === "string" && value.length > 0 ? value : undefined;
  }

  /** Applies runtime-configured context mappings to the multipart payload. */
  private appendConfiguredContextParams(
    formData: FormData,
    activityLogContext: ActivityLogContext,
    addActivityEndpointParams: ActivityLogAddActivityEndpointParamConfig[] | undefined
  ): void {
    if (!addActivityEndpointParams?.length) {
      return;
    }

    for (const endpointParam of addActivityEndpointParams) {
      const contextValue = activityLogContext[endpointParam.contextKey];
      const resolvedValue = this.resolveEndpointParamValue(endpointParam, contextValue);

      if (this.isMissingContextValue(resolvedValue)) {
        if (endpointParam.required) {
          throw new Error(
            `ActivityLogGridDialogService: Required activityLogContext key '${endpointParam.contextKey}' is missing.`
          );
        }

        continue;
      }

      formData.append(endpointParam.formDataKey, String(resolvedValue));
    }
  }

  /** Resolves endpoint parameter values using context, defaults, and known runtime fallbacks. */
  private resolveEndpointParamValue(
    endpointParam: ActivityLogAddActivityEndpointParamConfig,
    contextValue: unknown
  ): unknown {
    if (!this.isMissingContextValue(contextValue)) {
      return contextValue;
    }

    if (!this.isMissingContextValue(endpointParam.defaultValue)) {
      return endpointParam.defaultValue;
    }

    if (endpointParam.formDataKey.toLowerCase() === "partid") {
      return this.user.getPartId() ?? 0;
    }

    return undefined;
  }

  /** Returns true when endpoint param mappings include a payload key (case-insensitive). */
  private hasEndpointParamMapping(
    addActivityEndpointParams: ActivityLogAddActivityEndpointParamConfig[] | undefined,
    formDataKey: string
  ): boolean {
    if (!addActivityEndpointParams?.length) {
      return false;
    }

    return addActivityEndpointParams.some((param) => param.formDataKey.toLowerCase() === formDataKey.toLowerCase());
  }

  /** Returns true for undefined/null/blank-string values that should be treated as missing. */
  private isMissingContextValue(value: unknown): boolean {
    if (value === undefined || value === null) {
      return true;
    }

    if (typeof value === "string") {
      return value.trim().length < 1;
    }

    return false;
  }
}
